/**
 * The interface locales VoteMate ships. Data, not logic.
 *
 * Declared three times — here, mobile's copy, and `scripts/i18n/locales.mjs`;
 * CI asserts all three match. Adding a locale means a row in each, plus catalogs.
 *
 * Codes are BCP-47, canonical casing. `zh-Hans`/`zh-Hant` carry script subtags,
 * so truncating at the first hyphen collapses both into an unsupported bare `zh`.
 */
export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English', short: 'EN', dir: 'ltr', script: 'Latn' },
  { code: 'es', label: 'Español', short: 'ES', dir: 'ltr', script: 'Latn' },
  { code: 'zh-Hans', label: '简体中文', short: '简', dir: 'ltr', script: 'Hans' },
  { code: 'zh-Hant', label: '繁體中文', short: '繁', dir: 'ltr', script: 'Hant' },
  { code: 'vi', label: 'Tiếng Việt', short: 'VI', dir: 'ltr', script: 'Latn' },
  { code: 'ko', label: '한국어', short: '한', dir: 'ltr', script: 'Kore' },
  { code: 'tl', label: 'Tagalog', short: 'TL', dir: 'ltr', script: 'Latn' },
  { code: 'km', label: 'ភាសាខ្មែរ', short: 'ខ្មែរ', dir: 'ltr', script: 'Khmr' },
  { code: 'bn', label: 'বাংলা', short: 'বাং', dir: 'ltr', script: 'Beng' },
  { code: 'ja', label: '日本語', short: '日本', dir: 'ltr', script: 'Jpan' },
  { code: 'ar', label: 'العربية', short: 'ع', dir: 'rtl', script: 'Arab' },
  { code: 'fa', label: 'فارسی', short: 'فا', dir: 'rtl', script: 'Arab' },
  { code: 'ur', label: 'اردو', short: 'اُر', dir: 'rtl', script: 'Arab' },
  { code: 'ru', label: 'Русский', short: 'RU', dir: 'ltr', script: 'Cyrl' },
  { code: 'ht', label: 'Kreyòl Ayisyen', short: 'HT', dir: 'ltr', script: 'Latn' },
  { code: 'pt', label: 'Português', short: 'PT', dir: 'ltr', script: 'Latn' },
] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];
export type SupportedLanguageCode = SupportedLanguage['code'];

export const SUPPORTED_LANGUAGE_CODES = SUPPORTED_LANGUAGES.map(
  (l) => l.code
) as readonly SupportedLanguageCode[];

export const DEFAULT_LANGUAGE: SupportedLanguageCode = 'en';

/** Locales that read right to left. Drives layout mirroring. */
export const RTL_LANGUAGE_CODES = SUPPORTED_LANGUAGES.filter((l) => l.dir === 'rtl').map(
  (l) => l.code
) as readonly SupportedLanguageCode[];

export const isRTL = (code: string): boolean =>
  RTL_LANGUAGE_CODES.includes(code as SupportedLanguageCode);

export const getLanguage = (code: string): SupportedLanguage | undefined =>
  SUPPORTED_LANGUAGES.find((l) => l.code === code);

/** Lowercased code → canonical code, for case-insensitive matching. */
const BY_LOWERCASE = new Map<string, SupportedLanguageCode>(
  SUPPORTED_LANGUAGES.map((l) => [l.code.toLowerCase(), l.code])
);

/**
 * Region tags that need an explicit catalog. Only Chinese: there is no bare `zh`
 * catalog, so a region tag has to pick Simplified or Traditional.
 *
 * `fil` is deliberately absent — Filipino is a separate language from Tagalog,
 * so it falls through to English rather than being served `tl` strings.
 */
const ALIASES: Record<string, SupportedLanguageCode> = {
  zh: 'zh-Hans',
  'zh-cn': 'zh-Hans',
  'zh-sg': 'zh-Hans',
  'zh-tw': 'zh-Hant',
  'zh-hk': 'zh-Hant',
  'zh-mo': 'zh-Hant',
};

/**
 * Normalize an arbitrary language tag to a supported code, or `null`.
 *
 * Order: exact → alias → base subtag → alias of base subtag. Always returns
 * canonical casing (`zh-Hans`, never `zh-hans`) so stored, sent, and displayed
 * values are one spelling. `null` means callers fall back to English.
 */
export const resolveSupportedLanguage = (raw: string | null): SupportedLanguageCode | null => {
  if (!raw) return null;
  const normalized = raw.trim().toLowerCase();
  if (!normalized) return null;

  const exact = BY_LOWERCASE.get(normalized);
  if (exact) return exact;

  const alias = ALIASES[normalized];
  if (alias) return alias;

  const base = normalized.split('-')[0];
  return BY_LOWERCASE.get(base) ?? ALIASES[base] ?? null;
};
